import React, { useCallback, useEffect, useState } from 'react';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import DataSources from '../Step/DataSources';
import NameDescription from '../Step/NameDescription';
import Instruction from '../Step/Instructions';
import ExampleOutput from '../Step/ExampleOutput';
import ModelSelectionPreview from '../Step/ModelSelectionPreview';
import Schedule from '../Step/Schedule';
import { toast } from 'react-toastify';
import { callTokenApi } from '../../../../../../apiCaller';
import { API_AI_INSIGHTS_TEMPLATES_BY_ID, COMPONENT_NAME, TYPE_SHOW_UNSAVE_CHANGE } from '../../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { setRuleMultiHaveEditing } from '../../../../../../actions/common';

export const AIInsightsDetailContext = React.createContext({});

const AIInsightsTemplate = ({ id, attributes }) => {
     const dispatch = useDispatch();
     const { viewTemplates, setViewTemplates, models, setReFreshTemplate, setIsReFreshLogs } = attributes;
     const [isLoading, setIsLoading] = useState(false);
     const [activeStep, setActiveStep] = useState(1);
     const [stepsData, setStepsData] = useState({});
     const [initStepsData, setInitStepsData] = useState({});
     const [unsavedEdits, setUnsavedEdits] = useState({});
     const editMultiRule  = useSelector((state) => state.theme.editMultiRule);
     const [state, setState] = useState({
          accordion: '',
          isSubmitting: false,
          modelSelections: [],
     });

     useEffect(() => {
          const loadTemplateData = async () => {
               if (!id) return;

               setIsLoading(true);
               try {
                    const templateResponse = await callTokenApi(API_AI_INSIGHTS_TEMPLATES_BY_ID.replace(':id', id), 'GET');
                    const templateData = templateResponse?.data?.data;

                    if (templateResponse?.status === 200 && templateData) {
                         const dataSources = templateData?.dataSources?.map((sourceItem) => {
                              const parseDescriptionOverride = typeof sourceItem?.descriptionOverride === 'string' ? JSON.parse(sourceItem?.descriptionOverride) : sourceItem?.descriptionOverride;
                              const descriptionOverride = {
                                   ...parseDescriptionOverride,
                                   reportMetrics: parseDescriptionOverride?.reportMetrics.filter((metric) => !['Profile', 'View in Salesforce'].includes(metric?.metricName))
                              }

                              return {
                                   ...sourceItem,
                                   descriptionOverride: JSON.stringify(descriptionOverride),
                              }
                         });

                         setStepsData({
                              ...templateData,
                              dataSources,
                         });
                    } else {
                         toast.error('Failed to load template data.');
                    }
               } catch (error) {
                    console.error('Fetch template data error:', error);
                    toast.error('An error occurred while loading template data.');
               } finally {
                    setIsLoading(false);
               }
          };

          loadTemplateData();
     }, [id]);

     // --- Unsaved Changes Handling ---
    // Called by child components when they have unsaved changes
    const handleSetUnsavedChanges = useCallback((hasChanges) => {
          // Only dispatch if the 'show' status actually needs to change
          // to prevent unnecessary dispatches and potential re-renders.
          if (hasChanges && (!editMultiRule || !editMultiRule.show)) {
               dispatch(setRuleMultiHaveEditing({ show: true, type: TYPE_SHOW_UNSAVE_CHANGE.EDIT_SIMPLE, component: COMPONENT_NAME.AI_INSIGHTS_TEMPLATE }));
          } else if (!hasChanges && editMultiRule?.show) {
               // This 'else if' allows children to signal they are no longer dirty,
               // e.g., after reverting changes manually before trying to navigate.
               
               dispatch(setRuleMultiHaveEditing({ show: false, type: '', component: '' }));
          }
     }, [dispatch, editMultiRule]);

     // Called by child components after successfully saving or discarding changes
     const clearUnsavedChangesFlag = useCallback(() => {
          if (editMultiRule?.show) { // Check if the flag is actually set
               dispatch(setRuleMultiHaveEditing({ show: false, type: '', component: '' }));
          }
     }, [dispatch, editMultiRule]);

     // Callback for children to update their unsaved edits
     const updateUnsavedEdits = useCallback((stepId, stepFieldValues) => {
          setUnsavedEdits(prev => ({
               ...prev,
               [stepId]: { ...(prev[stepId] || {}), ...stepFieldValues } // Merge new values for the specific step
          }));
     }, []); // No dependency needed
     
     // Callback for children to clear their unsaved edits after saving
     const clearUnsavedEditsForStep = useCallback((stepId) => {
          setUnsavedEdits(prev => {
               const newState = { ...prev };
               delete newState[stepId]; // Remove entry for the step
               return newState;
          });
     }, []);
     
     // Callback for discard action (clears unsaved edits for the current step)
     // This might be triggered by the global popup, needing the activeStepId
     const discardUnsavedEditsForStep = useCallback((stepId) => {
          clearUnsavedEditsForStep(stepId); // Clear the temporary data
          // The global Redux flag also needs to be cleared - ensure popup's discard action does this
          // Or call clearUnsavedChangesFlag() here if appropriate
          if (typeof clearUnsavedChangesFlag === 'function') { // Assuming context provides this
               clearUnsavedChangesFlag();
          }
          // Close the popup?
          dispatch(handleSetUnsavedChanges(false)); // Assuming this action hides the popup
     
     }, [clearUnsavedEditsForStep, dispatch, clearUnsavedChangesFlag, handleSetUnsavedChanges]); // Add dependencies

     return (
          <React.Suspense fallback={<CenterSpinner />}>
               <div className="rule-detail account-connected-detail ai-insights-templates">
                    {isLoading ? (
                         <CenterSpinner />
                    ) : (
                         <div className="cvr-create-new">
                              <h1 className='pb-3'>{id ? `Edit: AI Insights Template ${stepsData.name}` : 'Create: AI Insights Template'}</h1>

                              <AIInsightsDetailContext.Provider
                                   value={{
                                        id,
                                        activeStep,
                                        setActiveStep,
                                        initStepsData,
                                        setInitStepsData,
                                        stepsData,
                                        setStepsData,
                                        viewTemplates,
                                        setViewTemplates,
                                        handleSetUnsavedChanges,
                                        clearUnsavedChangesFlag,
                                        unsavedEdits,
                                        updateUnsavedEdits,
                                        clearUnsavedEditsForStep,
                                        discardUnsavedEditsForStep,
                                        setReFreshTemplate,
                                        setIsReFreshLogs,
                                        state,
                                        setState
                                   }}
                              >
                                   <NameDescription />
                                   <DataSources />
                                   <Instruction />
                                   <ExampleOutput />
                                   <ModelSelectionPreview models={models} />
                                   <Schedule />
                              </AIInsightsDetailContext.Provider>
                         </div>
                    )}
               </div>
          </React.Suspense>
     );
};

export default AIInsightsTemplate;
